import { WEAPONS, type WeaponClass, type WeaponSpec, type WeaponSide } from "@hupper/shared/weapons";
import { ARMOR_ITEMS, armorPurchaseCost, getArmorItem, HELMET_ONLY_COST, type ArmorItem } from "@hupper/shared";
import type { GameClient, MatchHudState, RemotePlayerState } from "../net/GameClient";
import { weaponIconSvg, grenadeIconSvg } from "./weaponIcons";

/**
 * Buy menu — `B` toggles during buy / warmup.
 *
 * CS:GO-style visual grid (redesign):
 *
 *   1. ONE screen, five side-by-side columns — Equipment, Pistols, Mid-Tier,
 *      Rifles (snipers folded in, like CS:GO) and Grenades. Every item the
 *      player's side can buy is visible at once; no drill-down.
 *
 *   2. Each item is a visual CARD — number badge, name, the same rendered
 *      weapon icon the HUD uses (ui/weaponIcons), and its price. Owned /
 *      equipped / unaffordable states tint the card.
 *
 *   3. ONE click listener on the root — event delegation by `data-action`
 *      + `data-id`. Clicking a card buys/equips; clicking a column header
 *      focuses that column for number-key buys.
 *
 *   4. CS:GO hotkeys — press a column number (1-5) to focus it, then an item
 *      number (1-9) to buy the Nth card. Backspace / `0` unfocuses.
 *
 *   5. Side filter — T players see T / both items, CT see CT / both. The
 *      server enforces the same rule in `buy_weapon` (defense in depth).
 *
 *   6. Minimal header — only the buy timer and the player's money, themed to
 *      the player's side (CT blue / T gold). No title, no instructions.
 *
 * The previous two-step drill-down lives at `src/legacy/ui/BuyMenu.ts`.
 */

/** A buy-menu column + its number-key. */
interface ColumnDef {
  hotkey: string;
  label: string;
  /** WeaponClasses feeding this column. Rifles folds in snipers like CS:GO. */
  classes: WeaponClass[];
  /** Equipment column lists ARMOR_ITEMS, not weapons. */
  equipment?: boolean;
}

const COLUMNS: ColumnDef[] = [
  { hotkey: "1", label: "Equipment", classes: [], equipment: true },
  { hotkey: "2", label: "Pistols", classes: ["pistol"] },
  { hotkey: "3", label: "Mid-Tier", classes: ["smg"] },
  { hotkey: "4", label: "Rifles", classes: ["rifle", "sniper"] },
  { hotkey: "5", label: "Grenades", classes: ["grenade"] },
];

/** Rendered PNG armor icons (in /public/armor), warm-white like the weapon
 *  icons: vest = kevlar, helmet = helmet-only ($350), both = kevlar + helmet. */
const ARMOR_IMG: Record<"vest" | "helmet" | "both", string> = {
  vest: "/armor/vest.png",
  helmet: "/armor/helmet.png",
  both: "/armor/vest_helmet.png",
};

/** Armor card icon — rendered PNG matching the weapon-icon style. */
function armorIconHtml(kind: "vest" | "helmet" | "both"): string {
  return `<img class="bc-armor-img" src="${ARMOR_IMG[kind]}" alt="" aria-hidden="true" draggable="false" />`;
}

/** Helmet-only top-up (full armor, no helmet) reads as just "Helmet" at $350. */
function isHelmetOnly(item: ArmorItem, cost: number | null): boolean {
  return item.givesHelmet && cost === HELMET_ONLY_COST;
}

function armorLabel(item: ArmorItem, cost: number | null): string {
  return isHelmetOnly(item, cost) ? "Helmet" : item.name;
}

function armorIconKind(item: ArmorItem, cost: number | null): "vest" | "helmet" | "both" {
  if (!item.givesHelmet) return "vest";
  return isHelmetOnly(item, cost) ? "helmet" : "both";
}

/** Weapon icon — grenades use the color-coded utility art, others the HUD silhouette. */
function itemIconHtml(spec: WeaponSpec): string {
  return spec.class === "grenade" ? grenadeIconSvg(spec.id) : weaponIconSvg(spec);
}

type CardState = "buy" | "broke" | "owned" | "equipped";

export class BuyMenu {
  private readonly client: GameClient;
  private readonly root: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private readonly timerEl: HTMLSpanElement;
  private readonly moneyEl: HTMLSpanElement;
  private readonly bodyEl: HTMLDivElement;
  private readonly toastEl: HTMLDivElement;
  private toastTimer: number | null = null;
  private readonly unsubs: Array<() => void> = [];
  private visible = false;
  private lastPhase = "";
  /** null = no column focused; otherwise the focused column hotkey. */
  private currentColumn: string | null = null;
  /** Signature of the buy-relevant self fields the cards render. Used to skip
   *  rebuilding the card DOM on irrelevant snapshot churn (position / yaw /
   *  ammo / lastProcessedInputSeq tick every frame while the menu is open —
   *  rebuilding then destroys the pressed button between mousedown and mouseup,
   *  so the buy click is lost). */
  private lastShopSig = "";

  constructor(client: GameClient) {
    this.client = client;

    this.root = document.createElement("div");
    this.root.id = "buy-menu";
    this.root.className = "hidden";
    this.root.innerHTML = `
      <div class="buy-panel" data-buy-panel>
        <div class="buy-topbar">
          <div class="buy-time">
            <span class="buy-time-label">Buy Time Remaining</span>
            <span class="buy-timer"></span>
          </div>
          <span class="buy-money"></span>
        </div>
        <div class="buy-body"></div>
        <div class="buy-toast" data-toast></div>
      </div>`;
    this.panel = this.root.querySelector("[data-buy-panel]") as HTMLDivElement;
    this.timerEl = this.root.querySelector(".buy-timer") as HTMLSpanElement;
    this.moneyEl = this.root.querySelector(".buy-money") as HTMLSpanElement;
    this.bodyEl = this.root.querySelector(".buy-body") as HTMLDivElement;
    this.toastEl = this.root.querySelector("[data-toast]") as HTMLDivElement;
    document.body.appendChild(this.root);

    // Body-class flag so other systems (pointer-lock, slot hotkeys) know to
    // keep their hands off while the menu owns input.
    this.root.addEventListener("click", this.onClick);

    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) return;

      if (e.code === "KeyB" && !e.repeat) {
        e.preventDefault();
        this.toggle();
        return;
      }
      if (!this.visible) return;
      if (e.code === "Escape") {
        e.preventDefault();
        this.hide();
        return;
      }
      if (e.code === "Backspace" || e.code === "Digit0" || e.code === "Numpad0") {
        if (this.currentColumn !== null) {
          e.preventDefault();
          this.setColumn(null);
        }
        return;
      }
      // Number row → focus a column or buy the Nth card in the focused one.
      const digit = digitFromCode(e.code);
      if (digit !== null) {
        e.preventDefault();
        this.handleDigit(digit);
      }
    };
    window.addEventListener("keydown", onKey);
    this.unsubs.push(() => window.removeEventListener("keydown", onKey));

    this.unsubs.push(
      this.client.onMatchHud((h) => {
        this.lastPhase = h.phase;
        this.updateHeader(h);
        if (this.visible && h.phase !== "buy" && h.phase !== "warmup") this.hide();
        if (this.visible) this.renderShopIfChanged();
      }),
    );
    const refreshOnSelf = (_: unknown, sid: string) => {
      if (sid !== this.client.sessionId) return;
      if (this.visible) this.renderShopIfChanged();
    };
    this.unsubs.push(this.client.onPlayerAdd(refreshOnSelf));
    this.unsubs.push(this.client.onPlayerChange(refreshOnSelf));
  }

  destroy(): void {
    for (const u of this.unsubs) u();
    this.root.remove();
  }

  // ── Visibility ────────────────────────────────────────────────────────

  private toggle(): void {
    if (this.lastPhase !== "buy" && this.lastPhase !== "warmup") {
      this.flash(`Buy menu only available in buy or warmup phase (current: ${this.lastPhase || "—"})`);
      return;
    }
    if (this.visible) this.hide();
    else this.show();
  }

  private show(): void {
    this.visible = true;
    this.currentColumn = null;
    this.root.classList.remove("hidden");
    document.body.classList.add("buy-open");
    try { document.exitPointerLock(); } catch { /* ignore */ }
    this.renderShop();
    this.panel.setAttribute("tabindex", "-1");
    this.panel.focus({ preventScroll: true });
  }

  private hide(): void {
    this.visible = false;
    this.currentColumn = null;
    this.root.classList.add("hidden");
    document.body.classList.remove("buy-open");
  }

  // ── Column focus state ────────────────────────────────────────────────

  private setColumn(hotkey: string | null): void {
    this.currentColumn = hotkey;
    if (this.visible) this.renderShop();
  }

  private handleDigit(d: number): void {
    if (this.currentColumn === null) {
      // Pick a column by hotkey.
      const col = COLUMNS.find((c) => c.hotkey === String(d));
      if (!col) return;
      this.setColumn(col.hotkey);
      return;
    }
    // Inside a column: buy the Nth visible card (1-based).
    const ids = this.currentItemIds();
    const idx = d - 1;
    if (idx < 0 || idx >= ids.length) {
      this.flash(`No item bound to ${d}`);
      return;
    }
    this.handleBuy(ids[idx]);
  }

  /** Buy ids for the focused column (weapons or armor), in display order. */
  private currentItemIds(): string[] {
    const col = COLUMNS.find((c) => c.hotkey === this.currentColumn);
    if (!col) return [];
    if (col.equipment) return ARMOR_ITEMS.map((a) => a.id);
    return this.itemsForColumn(col).map((w) => w.id);
  }

  // ── Click delegation ──────────────────────────────────────────────────

  private readonly onClick = (ev: MouseEvent) => {
    const target = (ev.target as HTMLElement | null)?.closest("[data-action]") as HTMLElement | null;
    if (!target) {
      if (ev.target === this.root) this.hide();
      return;
    }
    ev.stopPropagation();
    const action = target.dataset.action;
    const id = target.dataset.id ?? "";
    if (action === "buy") this.handleBuy(id);
    else if (action === "equip") this.handleEquip(id);
    else if (action === "category") this.setColumn(id || null);
    else if (action === "close") this.hide();
  };

  private handleBuy(weaponId: string): void {
    const self = this.client.getLatestSelf();
    if (!self) {
      this.flash("Connecting to server… try again in a moment.");
      return;
    }
    // Armor isn't a weapon — route it through its own path.
    const armorItem = getArmorItem(weaponId);
    if (armorItem) {
      this.handleBuyArmor(self, armorItem);
      return;
    }
    const spec = WEAPONS.find((w) => w.id === weaponId);
    if (!spec) {
      this.flash(`Unknown weapon: ${weaponId}`);
      return;
    }
    const playerSide = teamSide(self.team);
    if (spec.side !== "both" && spec.side !== playerSide) {
      this.flash(`${spec.name} is ${spec.side}-only. Your side: ${playerSide}.`);
      return;
    }
    const owned = new Set(self.ownedWeapons ?? []);
    if (owned.has(weaponId)) {
      this.client.sendWeaponEquip(weaponId);
      this.flash(`Equipped ${spec.name}.`);
      return;
    }
    const money = self.money ?? 0;
    if (money < spec.cost) {
      this.flash(`Not enough money for ${spec.name} ($${spec.cost} · you have $${money}).`);
      return;
    }
    console.log(`[BuyMenu] sendBuyWeapon(${weaponId})`);
    this.client.sendBuyWeapon(weaponId);
    this.flash(`Buying ${spec.name}…`);
  }

  private handleBuyArmor(self: RemotePlayerState, armor: ArmorItem): void {
    // CS:GO pricing — null = already maxed for this item; $350 helmet-only
    // when armor is full and helmetless. Server recomputes the same cost.
    const cost = armorPurchaseCost(armor, self.armor ?? 0, !!self.helmet);
    const label = armorLabel(armor, cost);
    if (cost === null) {
      this.flash(`Already have ${label}.`);
      return;
    }
    const money = self.money ?? 0;
    if (money < cost) {
      this.flash(`Not enough money for ${label} ($${cost} · you have $${money}).`);
      return;
    }
    console.log(`[BuyMenu] sendBuyWeapon(${armor.id})`);
    this.client.sendBuyWeapon(armor.id);
    this.flash(`Buying ${label}…`);
  }

  private handleEquip(weaponId: string): void {
    const spec = WEAPONS.find((w) => w.id === weaponId);
    console.log(`[BuyMenu] sendWeaponEquip(${weaponId})`);
    this.client.sendWeaponEquip(weaponId);
    if (spec) this.flash(`Equipped ${spec.name}.`);
  }

  // ── Render ────────────────────────────────────────────────────────────

  private updateHeader(h: MatchHudState): void {
    const self = this.client.getLatestSelf();
    const money = self?.money ?? 0;
    this.timerEl.textContent =
      h.phase === "warmup" ? "∞" : h.buySecondsLeft != null ? formatClock(h.buySecondsLeft) : "—";
    this.moneyEl.textContent = `$${money}`;
  }

  /** Weapons visible to the player in a given column (side-filtered). */
  private itemsForColumn(col: ColumnDef): WeaponSpec[] {
    const self = this.client.getLatestSelf();
    if (!self) return [];
    const side = teamSide(self.team);
    return WEAPONS.filter(
      (w) => col.classes.includes(w.class) && w.cost > 0 && (w.side === "both" || w.side === side),
    );
  }

  /** Buy-relevant signature of the local player — the only fields the cards
   *  render. Position / aim / ammo / lastProcessedInputSeq are excluded so the
   *  card DOM stays stable while the player moves with the menu open. */
  private shopSignature(): string {
    const self = this.client.getLatestSelf();
    if (!self) return "none";
    const owned = (self.ownedWeapons ?? []).slice().sort().join(",");
    return [self.team ?? "", self.money ?? 0, self.weaponId ?? "", self.armor ?? 0, self.helmet ? 1 : 0, owned].join("|");
  }

  /** Rebuild the cards only when a buy-relevant field changed. Skips the
   *  per-snapshot DOM teardown that was destroying the pressed button mid-click. */
  private renderShopIfChanged(): void {
    const sig = this.shopSignature();
    if (sig === this.lastShopSig) return;
    this.renderShop();
  }

  private renderShop(): void {
    const self = this.client.getLatestSelf();
    this.lastShopSig = this.shopSignature();
    this.bodyEl.replaceChildren();

    if (!self) {
      const loading = document.createElement("div");
      loading.className = "buy-loading";
      loading.textContent = "Connecting…";
      this.bodyEl.appendChild(loading);
      return;
    }

    // Side accent (CT blue / T gold) — drives borders, badges, highlights.
    const side = teamSide(self.team);
    this.panel.style.setProperty("--buy-accent", side === "T" ? "#ff8a5c" : "#5eb8ff");

    this.moneyEl.textContent = `$${self.money ?? 0}`;

    const cols = document.createElement("div");
    cols.className = "buy-cols";
    for (const col of COLUMNS) cols.appendChild(this.renderColumn(col, self));
    this.bodyEl.appendChild(cols);
  }

  private renderColumn(col: ColumnDef, self: RemotePlayerState): HTMLElement {
    const colEl = document.createElement("div");
    colEl.className = `buy-col${this.currentColumn === col.hotkey ? " active" : ""}`;

    const head = document.createElement("button");
    head.type = "button";
    head.className = "buy-col-head";
    head.dataset.action = "category";
    head.dataset.id = col.hotkey;
    head.innerHTML = `<span class="bch-key">${col.hotkey}</span><span class="bch-label">${escapeHtml(col.label)}</span>`;
    colEl.appendChild(head);

    const cards = document.createElement("div");
    cards.className = "buy-col-cards";
    if (col.equipment) this.renderArmorCards(cards, self);
    else this.renderWeaponCards(cards, col, self);
    colEl.appendChild(cards);
    return colEl;
  }

  private renderWeaponCards(parent: HTMLElement, col: ColumnDef, self: RemotePlayerState): void {
    const money = self.money ?? 0;
    const owned = new Set(self.ownedWeapons ?? ["knife", "g18"]);
    const equipped = self.weaponId;

    this.itemsForColumn(col).forEach((w, i) => {
      const has = owned.has(w.id);
      const isEquipped = equipped === w.id;
      const state: CardState = isEquipped ? "equipped" : has ? "owned" : money >= w.cost ? "buy" : "broke";
      const price = isEquipped ? "Equipped" : has ? "Owned" : `$${w.cost}`;
      parent.appendChild(
        this.makeCard({ action: has ? "equip" : "buy", id: w.id, n: i + 1, name: w.name, icon: itemIconHtml(w), price, state }),
      );
    });
  }

  private renderArmorCards(parent: HTMLElement, self: RemotePlayerState): void {
    const money = self.money ?? 0;
    const armor = self.armor ?? 0;
    const helmet = !!self.helmet;

    ARMOR_ITEMS.forEach((item, i) => {
      // CS:GO pricing — full vest + no helmet turns "Kevlar + Helmet" into a
      // $350 helmet-only top-up; null = already maxed for this item.
      const cost = armorPurchaseCost(item, armor, helmet);
      const owned = cost === null;
      const state: CardState = owned ? "owned" : money >= cost ? "buy" : "broke";
      const price = owned ? "Owned" : `$${cost}`;
      parent.appendChild(
        this.makeCard({
          action: "buy",
          id: item.id,
          n: i + 1,
          name: armorLabel(item, cost),
          icon: armorIconHtml(armorIconKind(item, cost)),
          price,
          state,
        }),
      );
    });
  }

  private makeCard(o: {
    action: "buy" | "equip";
    id: string;
    n: number;
    name: string;
    icon: string;
    price: string;
    state: CardState;
  }): HTMLButtonElement {
    const el = document.createElement("button");
    el.type = "button";
    el.className = `buy-card buy-card-${o.state}`;
    el.dataset.action = o.action;
    el.dataset.id = o.id;
    el.innerHTML = `
      <span class="bc-key">${o.n}</span>
      <span class="bc-name">${escapeHtml(o.name)}</span>
      <span class="bc-icon">${o.icon}</span>
      <span class="bc-price">${escapeHtml(o.price)}</span>
    `;
    return el;
  }

  // ── Toast ─────────────────────────────────────────────────────────────

  private flash(msg: string): void {
    if (!this.toastEl) return;
    this.toastEl.textContent = msg;
    this.toastEl.classList.add("show");
    if (this.toastTimer != null) window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => {
      this.toastEl.classList.remove("show");
      this.toastTimer = null;
    }, 2200);
  }
}

function teamSide(team: "ct" | "t" | undefined): WeaponSide {
  return team === "t" ? "T" : "CT";
}

/** Seconds → `M:SS` clock, CS:GO-style. */
function formatClock(secs: number): string {
  const s = Math.max(0, Math.floor(secs));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function digitFromCode(code: string): number | null {
  if (code.startsWith("Digit")) {
    const n = parseInt(code.slice(5), 10);
    return Number.isFinite(n) ? n : null;
  }
  if (code.startsWith("Numpad")) {
    const rest = code.slice(6);
    const n = parseInt(rest, 10);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
